/**
 * Public Market Insights (Phase 7A).
 *
 * Buyer/seller-facing neighbourhood insights: price ranges + demand/activity
 * bands per area. Entirely server-gated — when the admin's `publicInsights`
 * toggle is off the API 404s and we render a friendly "not available" state, so
 * this page is safe to ship before the feature is switched on.
 *
 * Owner, 2026-10-07: one table, not a wall of identical cards. Figures read best
 * in columns (each row a district, each column one measure), the demand and
 * activity levels are words with colour only when they are high, the city can be
 * narrowed, and the city names are translated.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import SaudiRiyalSymbol from '../../components/base/icons/SaudiRiyalSymbol';
import { getPublicAreas, type PublicAreaInsight, type DataLevel } from '../../services/marketInsightsService';
import { matchSaudiCity } from '../../config/saudiCities';
import { formatAmount } from '../../utils/numberFormat';
import { BrandRule } from '../../components/brand/BrandRule';

type Band = NonNullable<PublicAreaInsight['demandBand']>;

const PILL = 'inline-flex h-10 shrink-0 items-center rounded-full border px-4 text-[0.875rem] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30';
const PILL_ON = 'border-[var(--pv-color-primary)] bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)]';
const PILL_OFF = 'border-[var(--pv-border-strong)] bg-[var(--pv-color-surface)] text-[var(--pv-color-text)] hover:border-[var(--pv-color-primary)]';
const TH = 'px-4 py-3 text-start text-[0.8125rem] font-semibold text-[var(--pv-color-text-muted)]';
const TD = 'px-4 py-3 align-top tabular-nums';

export default function MarketInsightsPage() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [level, setLevel] = useState<DataLevel>('curated');
  const [areas, setAreas] = useState<PublicAreaInsight[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [city, setCity] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getPublicAreas();
        if (!alive) return;
        setAvailable(res.available);
        setLevel(res.level);
        setAreas(res.areas);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // The data stores the city in English; show it in the page's language.
  const cityName = (raw: string | null) => {
    if (!raw) return '';
    const match = matchSaudiCity(raw);
    return match ? (isAr ? match.nameAr : match.nameEn) : raw;
  };

  // The cities present, most districts first.
  const cities = useMemo(() => {
    const counts = new Map<string, number>();
    areas.forEach((a) => a.city && counts.set(a.city, (counts.get(a.city) ?? 0) + 1));
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([name]) => name);
  }, [areas]);
  const rows = useMemo(
    () => (city ? areas.filter((a) => a.city === city) : areas).slice().sort((a, b) => b.listings - a.listings),
    [areas, city],
  );

  const bandLabel = (b: Band) => {
    if (isAr) return b === 'high' ? 'مرتفع' : b === 'medium' ? 'متوسط' : 'منخفض';
    return b === 'high' ? 'High' : b === 'medium' ? 'Medium' : 'Low';
  };
  // Colour only where it says something: high is teal, the rest is plain text.
  const band = (b?: Band) =>
    b ? (
      <span className={b === 'high' ? 'font-semibold text-[var(--pv-color-primary)]' : b === 'medium' ? 'text-[var(--pv-color-text)]' : 'text-[var(--pv-color-text-muted)]'}>
        {bandLabel(b)}
      </span>
    ) : (
      <span className="text-[var(--pv-color-text-subtle)]">-</span>
    );
  const range = (bucket?: { minPrice: number; maxPrice: number }) =>
    bucket && (bucket.minPrice || bucket.maxPrice) ? (
      <span className="whitespace-nowrap">
        {formatAmount(bucket.minPrice, { maximumFractionDigits: 0 })} - {formatAmount(bucket.maxPrice, { maximumFractionDigits: 0 })}
      </span>
    ) : (
      <span className="text-[var(--pv-color-text-subtle)]">-</span>
    );
  const riyal = <SaudiRiyalSymbol className="ms-1 inline h-3 w-3 align-[-0.1em]" title={isAr ? 'ريال سعودي' : 'Saudi riyal'} />;
  const showActivity = level === 'activity';

  return (
    <div dir={isAr ? 'rtl' : 'ltr'} className="flex min-h-screen flex-col bg-[var(--pv-color-surface)]">
      <Navigation />
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 pb-16 pt-10 sm:px-6">
        <header>
          <h1 className="text-[1.75rem] font-bold text-[var(--pv-color-primary)] sm:text-[2rem]">
            {isAr ? 'رؤى السوق العقاري' : 'Market Insights'}
          </h1>
          <BrandRule className="mt-3" />
          <p className="mt-2 max-w-2xl text-[0.9375rem] text-[var(--pv-color-text-muted)]">
            {isAr
              ? 'نطاقات الأسعار ومؤشرات الطلب والنشاط حسب الحي، من إعلانات المنصة الفعلية.'
              : 'Price ranges plus demand and activity by neighbourhood, from real listings on the platform.'}
          </p>
        </header>

        {loading && (
          <div aria-busy="true" className="mt-8 space-y-2">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="h-12 animate-pulse rounded-lg bg-[var(--pv-color-surface-sunken)] motion-reduce:animate-none" />
            ))}
          </div>
        )}

        {!loading && error && (
          <p className="mt-8 text-[0.9375rem] text-[var(--pv-color-text-muted)]">
            {isAr ? 'تعذّر تحميل المؤشرات. حاول مرة أخرى لاحقاً.' : 'Could not load the insights. Please try again later.'}
          </p>
        )}

        {!loading && !error && !available && (
          <div className="mt-10 max-w-xl">
            <h2 className="text-[1.25rem] font-semibold text-[var(--pv-color-text)]">{isAr ? 'قريباً' : 'Coming soon'}</h2>
            <p className="mt-2 text-[0.9375rem] text-[var(--pv-color-text-muted)]">
              {isAr ? 'رؤى السوق العامة غير مفعّلة حالياً. تحقق لاحقاً.' : 'Public market insights are not enabled right now. Please check back later.'}
            </p>
          </div>
        )}

        {!loading && !error && available && areas.length === 0 && (
          <p className="mt-10 text-[0.9375rem] text-[var(--pv-color-text-muted)]">
            {isAr ? 'لا توجد بيانات كافية بعد.' : 'Not enough data to show yet.'}
          </p>
        )}

        {!loading && !error && available && areas.length > 0 && (
          <>
            {/* Narrow to one city */}
            {cities.length > 1 && (
              <div className="mt-6 flex gap-2 overflow-x-auto no-scrollbar" role="group" aria-label={isAr ? 'المدينة' : 'City'}>
                {['', ...cities].map((c) => (
                  <button
                    key={c || 'all'}
                    type="button"
                    aria-pressed={city === c}
                    onClick={() => setCity(c)}
                    className={`${PILL} ${city === c ? PILL_ON : PILL_OFF}`}
                  >
                    {c ? cityName(c) : isAr ? 'كل المدن' : 'All cities'}
                  </button>
                ))}
              </div>
            )}

            <p className="mt-4 text-[0.875rem] text-[var(--pv-color-text-muted)] tabular-nums">
              {isAr ? `${formatAmount(rows.length)} حي، الأكثر عروضاً أولاً` : `${formatAmount(rows.length)} districts, most listings first`}
            </p>

            {/* One row per district. Wide on phones: it scrolls sideways, the district stays put. */}
            <div className="mt-3 overflow-x-auto rounded-[16px] border border-[var(--pv-border-soft)]">
              <table className="w-full min-w-[46rem] border-collapse text-[0.9375rem] text-[var(--pv-color-text)]">
                <caption className="sr-only">
                  {isAr ? 'نطاقات الأسعار والطلب والنشاط لكل حي' : 'Price ranges, demand and activity per district'}
                </caption>
                <thead className="bg-[var(--pv-color-surface-sunken)]">
                  <tr>
                    <th scope="col" className={`${TH} sticky start-0 bg-[var(--pv-color-surface-sunken)]`}>{isAr ? 'الحي' : 'District'}</th>
                    <th scope="col" className={TH}>{isAr ? 'أسعار البيع' : 'Sale prices'}{riyal}</th>
                    <th scope="col" className={TH}>{isAr ? 'أسعار الإيجار' : 'Rent prices'}{riyal}</th>
                    <th scope="col" className={TH}>{isAr ? 'العروض النشطة' : 'Active listings'}</th>
                    <th scope="col" className={TH}>{isAr ? 'الطلب' : 'Demand'}</th>
                    <th scope="col" className={TH}>{isAr ? 'النشاط' : 'Activity'}</th>
                    {showActivity && (
                      <>
                        <th scope="col" className={TH}>{isAr ? 'مشاهدات' : 'Views'}</th>
                        <th scope="col" className={TH}>{isAr ? 'تواصل' : 'Contacts'}</th>
                        <th scope="col" className={TH}>{isAr ? 'مبيعات' : 'Sold'}</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.areaId} className="border-t border-[var(--pv-border-subtle)]">
                      <th scope="row" className="sticky start-0 bg-[var(--pv-color-surface)] px-4 py-3 text-start align-top font-semibold">
                        {isAr ? a.nameAr || a.name : a.name}
                        <span className="block text-[0.8125rem] font-normal text-[var(--pv-color-text-muted)]">
                          {cityName(a.city)}
                          {a.soldRecently ? ` · ${isAr ? 'مبيعات حديثة' : 'recent sales'}` : ''}
                        </span>
                      </th>
                      <td className={TD}>{range(a.price?.SALE)}</td>
                      <td className={TD}>{range(a.price?.RENT)}</td>
                      <td className={TD}>{formatAmount(a.listings)}</td>
                      <td className={TD}>{band(a.demandBand)}</td>
                      <td className={TD}>{band(a.activityBand)}</td>
                      {showActivity && (
                        <>
                          <td className={TD}>{formatAmount(a.views90d ?? 0)}</td>
                          <td className={TD}>{formatAmount(a.contacts90d ?? 0)}</td>
                          <td className={TD}>{formatAmount(a.sold90d ?? 0)}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <p className="mt-6 text-[0.8125rem] text-[var(--pv-color-text-muted)]">
          {isAr
            ? 'تُحدَّث المؤشرات دورياً وتعكس نشاط المنصة خلال آخر 90 يوماً. الأرقام إرشادية.'
            : 'Signals update periodically and reflect platform activity over the last 90 days. Figures are indicative.'}
        </p>
      </main>
      <Footer />
    </div>
  );
}
